import { useState } from 'react';
import {
  Ban, CircleCheck, CircleDashed, CircleOff, Clock, Eye, Loader2, RefreshCw, TriangleAlert, Zap, type LucideIcon,
} from 'lucide-react';
import * as api from '../worlds.api';
import { toast, useWorldsStore } from '../worlds.store';
import { fmtDate, fmtInt } from '../constants';
import { ROSTER_STATUS } from '../useWorldsStats';
import type { RosterPreview, RosterPushResult, RosterStatus } from '../worlds.types';
import { Modal } from './Modal';
import { EmptyRow, EmptyState, InfoBox, StatusBadge, cx } from './ui';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';
import cs from './countries.module.scss';

// ============================================================
// Synchro du roster fondable vers NGCountryAPI
// ============================================================
// Le satellite pousse tout seul (debounce de 10 s après une modification de pays) ; le bouton sert
// à forcer une tentative immédiate. Un 409 est un garde-fou (roster qui rétrécit, état plus récent
// côté API) : on propose alors « Forcer », jamais automatiquement.
// ============================================================

/** Icône du statut (libellé et ton : ROSTER_STATUS, partagé avec l'accueil). */
const STATUS_ICON: Record<RosterStatus, LucideIcon> = {
  disabled: CircleOff,
  idle: CircleDashed,
  pending: Clock,
  ok: CircleCheck,
  error: TriangleAlert,
  blocked: Ban,
};

const EXCLUDED_REASON: Record<RosterPreview['excluded'][number]['reason'], string> = {
  disabled: 'Désactivé',
  'no-center': 'Sans f home',
  'invalid-name': 'Nom non fondable',
};

function resultLine(r: RosterPushResult): string {
  if (r.noop) return `Aucun changement (${fmtInt(r.countries)} pays).`;
  const parts = [`${fmtInt(r.inserted)} ajouté(s)`, `${fmtInt(r.updated)} modifié(s)`, `${fmtInt(r.unlisted)} retiré(s)`, `${fmtInt(r.unchanged)} inchangé(s)`];
  return `${r.dryRun ? 'Simulation : ' : ''}${parts.join(', ')}.${r.takenUnlisted.length ? ` Fondés mais retirés : ${r.takenUnlisted.join(', ')}.` : ''}`;
}

export function RosterSyncBadge({ readOnly }: { readOnly: boolean }) {
  const sync = useWorldsStore((s) => s.state?.rosterSync ?? null);
  const roster = useWorldsStore((s) => s.roster);
  const loadRoster = useWorldsStore((s) => s.loadRoster);
  const poll = useWorldsStore((s) => s.poll);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);
  const [details, setDetails] = useState(false);
  const [preview, setPreview] = useState<RosterPreview | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  if (!sync) return null;
  const st = ROSTER_STATUS[sync.status] ?? ROSTER_STATUS.idle;
  const StIcon = STATUS_ICON[sync.status] ?? CircleDashed;

  const run = async (force: boolean) => {
    setBusy(true);
    setConflict(null);
    const res = await api.syncRoster({ force });
    setBusy(false);
    if (!res.data) {
      if (res.status === 409) setConflict(res.error ?? 'Refus du garde-fou de NGCountryAPI');
      else toast.error(res.error ?? 'Synchro en échec');
      void loadRoster();
      return;
    }
    useWorldsStore.setState({ roster: res.data.state });
    toast.success(res.data.result ? resultLine(res.data.result) : 'Roster à jour.');
    void poll();
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewError(null);
    const res = await api.getRosterPreview();
    if (res.data) setPreview(res.data); else setPreviewError(res.error ?? 'Aperçu indisponible');
  };

  const openDetails = () => { setDetails(true); void loadRoster(); };

  const detailSt = roster ? ROSTER_STATUS[roster.status] ?? ROSTER_STATUS.idle : null;
  const DetailIcon = roster ? STATUS_ICON[roster.status] ?? CircleDashed : CircleDashed;

  return (
    <div className={ws.rosterBox}>
      <StatusBadge tone={st.tone} icon={StIcon} onClick={openDetails}
        title={sync.lastError ? `${st.label} : ${sync.lastError}` : `${st.label} : détails de la synchro du roster`}>
        {st.label}
        {sync.lastSuccessAt && <span className={ws.pillSub}> · {fmtDate(sync.lastSuccessAt)}</span>}
      </StatusBadge>
      {!readOnly && (
        <button type="button" className={styles.secondaryBtn} onClick={() => void run(false)} disabled={busy || !sync.configured}
          title={sync.configured ? 'Pousser le roster maintenant' : 'Intégration NGCountryAPI absente du config.yaml du satellite (integrations.ngcountry-api)'}>
          {busy ? <Loader2 size={14} className={ws.spinning} aria-hidden /> : <RefreshCw size={14} aria-hidden />} Synchroniser en jeu
        </button>
      )}
      <button type="button" className={ws.linkBtn} onClick={() => void openPreview()}>
        <Eye size={13} aria-hidden /> Aperçu du roster
      </button>
      {conflict && (
        <div className={cs.rosterWarn}>
          <InfoBox tone="warn" title="Synchro refusée par le garde-fou de NGCountryAPI">
            {conflict}
            {!readOnly && (
              <div className={cs.rosterActions}>
                <button type="button" className={cx(styles.secondaryBtn, ws.dangerBtn)} disabled={busy} onClick={() => void run(true)}>
                  {busy ? <Loader2 size={14} className={ws.spinning} aria-hidden /> : <Zap size={14} aria-hidden />} Forcer l'envoi
                </button>
                <button type="button" className={styles.secondaryBtn} onClick={() => setConflict(null)}>Ignorer</button>
              </div>
            )}
          </InfoBox>
        </div>
      )}

      {details && (
        <Modal title="Synchro du roster (NGCountryAPI)" icon={<Zap size={16} aria-hidden />} onClose={() => setDetails(false)}
          subtitle="Roster fondable poussé vers NGCountryAPI : pays actifs avec un f home."
          footer={<button type="button" className={styles.secondaryBtn} onClick={() => setDetails(false)}>Fermer</button>}>
          {!roster || !detailSt ? (
            <EmptyState icon={Loader2} spin compact title="Lecture de la synchro…" />
          ) : (
            <dl className={ws.kv}>
              <dt>Statut</dt>
              <dd>
                <StatusBadge tone={detailSt.tone} icon={DetailIcon}>{detailSt.label}</StatusBadge>
                {roster.inSync && <> <StatusBadge tone="ok" icon={CircleCheck}>à jour</StatusBadge></>}
              </dd>
              <dt>Intégration</dt><dd>{roster.configured ? <>configurée · monde <code>{roster.level}</code></> : 'absente (integrations.ngcountry-api du config.yaml)'}</dd>
              <dt>Projection</dt><dd>version {roster.current.version} · {fmtInt(roster.current.countries)} pays · <code>{roster.current.sha256.slice(0, 16)}…</code></dd>
              <dt>Dernier envoi</dt><dd>{roster.pushed ? `version ${roster.pushed.version} le ${fmtDate(roster.pushed.at)}` : 'jamais'}</dd>
              <dt>Dernier succès</dt><dd>{roster.lastSuccessAt ? fmtDate(roster.lastSuccessAt) : 'aucun'}</dd>
              {roster.pendingSince && <><dt>En attente depuis</dt><dd>{fmtDate(roster.pendingSince)}{roster.nextAttemptAt ? ` · prochain essai ${fmtDate(roster.nextAttemptAt)}` : ''}</dd></>}
              {roster.lastError && <><dt>Dernière erreur</dt><dd className={ws.textBad}>{roster.lastError}</dd></>}
              {roster.lastResult && <><dt>Dernier résultat</dt><dd>{resultLine(roster.lastResult)}</dd></>}
              <dt>Côté API</dt>
              <dd>
                {roster.remote
                  ? `source ${roster.remote.source ?? 'inconnue'} · révision ${roster.remote.revision} · ${fmtInt(roster.remote.listed)} listés · ${fmtInt(roster.remote.taken)} fondés${roster.remote.appliedAt ? ` · appliqué le ${fmtDate(roster.remote.appliedAt)}` : ''}`
                  : 'injoignable ou non configurée'}
              </dd>
            </dl>
          )}
        </Modal>
      )}

      {previewOpen && (
        <Modal title="Aperçu du roster fondable" icon={<Eye size={16} aria-hidden />} size="wide" onClose={() => setPreviewOpen(false)}
          subtitle={preview
            ? `${fmtInt(preview.countries.length)} pays envoyés à NGCountryAPI · monde ${preview.level} · version ${preview.version}`
            : 'Ce que le satellite envoie à NGCountryAPI.'}
          footer={<button type="button" className={styles.secondaryBtn} onClick={() => setPreviewOpen(false)}>Fermer</button>}>
          {previewError && <InfoBox tone="bad" title="Aperçu indisponible">{previewError}</InfoBox>}
          {!preview && !previewError && <EmptyState icon={Loader2} spin compact title="Calcul de l'aperçu…" />}
          {preview && (
            <>
              <p className={styles.muted}>
                Empreinte <code>{preview.sha256.slice(0, 16)}…</code>
                {preview.excluded.length > 0 ? ` · ${fmtInt(preview.excluded.length)} pays exclu(s), détail ci-dessous.` : ' · aucun pays exclu.'}
              </p>
              {preview.excluded.length > 0 && (
                <div className={ws.tableWrap}>
                  <table className={ws.table}>
                    <thead><tr><th>Exclu</th><th>Raison</th></tr></thead>
                    <tbody>
                      {preview.excluded.map((e) => (
                        <tr key={e.countryId}>
                          <td><span className={cs.slug}>{e.slug}</span></td>
                          <td>
                            <StatusBadge tone={e.reason === 'disabled' ? 'off' : 'warn'}
                              icon={e.reason === 'disabled' ? CircleOff : TriangleAlert}>
                              {EXCLUDED_REASON[e.reason] ?? e.reason}
                            </StatusBadge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className={ws.tableWrap}>
                <table className={ws.table}>
                  <thead><tr><th>Nom</th><th className={ws.num}>X</th><th className={ws.num}>Z</th><th>Monde</th></tr></thead>
                  <tbody>
                    {preview.countries.map((c) => (
                      <tr key={c.name}>
                        <td><span className={cs.slug}>{c.name}</span></td>
                        <td className={cx(ws.num, ws.mono)}>{c.x}</td>
                        <td className={cx(ws.num, ws.mono)}>{c.z}</td>
                        <td><code className={ws.codeChip}>{c.worldName}</code></td>
                      </tr>
                    ))}
                    {preview.countries.length === 0 && (
                      <EmptyRow colSpan={4} icon={CircleDashed} title="Roster vide"
                        text="Aucun pays actif avec un f home : rien n'est proposé à la fondation." />
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
